/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

// Map a phone-verification SEND failure to friendly copy. Used by both the
// web path (Firebase rejects with an `auth/*` code) and the iOS native path
// (the @capacitor-firebase/authentication `phoneVerificationFailed` event
// carries only a raw `message` — no code). Without this the native path
// surfaced Firebase's raw anti-abuse string ("We have blocked all requests
// from this device due to unusual activity. Try again later.") verbatim.

export function phoneVerifyErrorMessage(input: {
  code?: string;
  message?: string;
}): string {
  const code = input.code ?? "";
  const msg = (input.message ?? "").toLowerCase();
  // NOTE: the `auth/too-many-requests` ("too many verification attempts… use a
  // different number") branch was intentionally REMOVED — this case now falls
  // through to the neutral generic message below. Re-add later if we want
  // dedicated anti-abuse copy.
  if (
    code === "auth/invalid-phone-number" ||
    (msg.includes("invalid") && msg.includes("phone"))
  ) {
    return "That phone number doesn't look valid.";
  }
  // iOS: Firebase's reCAPTCHA "verify app" sheet was dismissed
  // (ERROR_WEB_CONTEXT_CANCELLED / code 17058). Not an error — gently prompt
  // the user to retry rather than showing a scary failure message.
  if (
    msg.includes("cancel") ||
    msg.includes("web_context_cancel") ||
    code === "17058"
  ) {
    return "Verification was cancelled. Tap Send code to try again.";
  }
  return "Couldn't send verification code. Try again.";
}
